import AMapLoader from "@amap/amap-jsapi-loader";
import { Box } from "@mui/material";
import LayerSwitch from "containers/map/layer-switch/layer-switch";
import WorldZoom from "containers/map/world-zoom/world-zoom";
import Zooms from "containers/map/zooms/zooms";
import { useEffect, useRef, useState } from "react";

const amapkey = "aee6edc014f6b2e1f310095a5c40a949";

interface AMapNoMatchProps {
  readonly center?: [number, number];
  readonly zoom?: number;
  readonly data?: any[];
  readonly selectOnMap?: any;
  readonly setSelectOnMap?: (item: any) => void;
  readonly selectedId?: string | null;
  readonly onMarkerClick?: (id: string) => void;
  readonly onMapClick?: (coords: { lng: number; lat: number }) => void;
}

function AMapNoMatch({
  center = [113, 30],
  zoom = 3,
  data = [],
  selectedId,
  selectOnMap,
  setSelectOnMap,
  onMarkerClick,
  onMapClick
}: AMapNoMatchProps) {
  const containerIdRef = useRef(
    `mini-map-${new Date().getTime()}`
  );
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const AMapRef = useRef<any>(null);
  const prevSelectedIdRef = useRef<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  useEffect(() => {
    const isUnmounted = { current: false };
    (window as any)._AMapSecurityConfig = {
      securityJsCode: "1ff8751ba0f4086ec444b1acbb56f14a"
    };

    let map: any;
    AMapLoader.load({
      key: amapkey,
      version: "2.0",
      plugins: ["AMap.Marker", "AMap.ToolBar"]
    })
      .then((AMap) => {
        if (isUnmounted.current) {
          return;
        }
        AMapRef.current = AMap;
        map = new AMap.Map(containerIdRef.current, {
          zoom: zoom,
          center: center,
          resizeEnable: true
        });
        mapRef.current = map;

        const scale = new AMap.Scale({
          position: { bottom: "10px", right: "30px" },
        });
        map.addControl(scale);

        // const toolBar = new AMap.ToolBar();
        // map.addControl(toolBar);
        setIsMapReady(true);
      })
      .catch((e) => console.log(e));

    return () => {
      isUnmounted.current = true;
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
      setIsMapReady(false);
    };
  }, []);

  // Effect for map click
  useEffect(() => {
    if (!isMapReady || !mapRef.current) {
      return;
    }

    const map = mapRef.current;

    if (!onMapClick || !selectOnMap) {
      //移除地图点击的监听事件，将鼠标的样式恢复为默认
      map.setDefaultCursor("grab");
      // 移除所有点击事件监听器，以防万一
      map.off("click");
      return;
    }

    map.setDefaultCursor("crosshair");
    const clickHandler = (e: any) => {
      onMapClick({
        ...selectOnMap,
        longitude: e.lnglat.getLng(),
        latitude: e.lnglat.getLat()
      });
      
      // 地图点击监听，只让鼠标在地图点击一次，记录经纬度，点击一次后，关闭监听
      //需要改为：地图点击监听，可以让鼠标点击多次，记录最新一次的经纬度，或者每次记录更新，不关闭监听
    };
    
    map.on("click", clickHandler);

    return () => {
      map.setDefaultCursor("grab");
      map.off("click", clickHandler);
    };
  }, [isMapReady, selectOnMap, onMapClick]);

  // Effect for creating/re-creating markers when data changes
  useEffect(() => {
    if (!isMapReady || !mapRef.current || !AMapRef.current) {
      return;
    }
    
    const AMap = AMapRef.current;

    // 清除旧标记
    mapRef.current.remove(markersRef.current);
    markersRef.current = [];

    const markers: any[] = [];
    
    data?.forEach((item) => {
      if (item.longitude && item.latitude) {
        const isSelected = item.id === selectedId;
        const size = isSelected ? { width: 30, height: 42 } : { width: 20, height: 28 };
        const marker = new AMap.Marker({
          position: [item.longitude, item.latitude],
          extData: { id: item.id },
          content: `<img src="/icon_map_pin_yellow.svg" style="width:${size.width}px;height:${size.height}px;"></img>`,
          offset: new AMap.Pixel(-size.width / 2, -size.height),
          zIndex: isSelected ? 110 : 100
        });
        marker.on("click", (e: any) => {
          if (onMarkerClick) {
            onMarkerClick(e.target.getExtData().id);
          }
        });
        markers.push(marker);
      }
    });

    markersRef.current = markers;

    if (markers.length > 0) {
      mapRef.current.add(markers);
      if (!selectedId) {
        // setFitView(overlays, immediately, avoid, maxZoom)
        // avoid: [top, right, bottom, left]
        mapRef.current.setFitView(undefined, false, [60, 60, 60, 60], 6);
      }
    }
  }, [isMapReady, data, onMarkerClick, selectedId]);

  // Effect for updating marker styles when selectedId changes
  useEffect(() => {
    if (
      !isMapReady ||
      !mapRef.current ||
      !AMapRef.current ||
      markersRef.current.length === 0
    ) {
      return;
    }

    const AMap = AMapRef.current;

    const updateMarkerStyle = (marker: any, isSelected: boolean) => {
      const size = isSelected ? { width: 30, height: 42 } : { width: 20, height: 28 };
      marker.setContent(
        `<img src="/icon_map_pin_yellow.svg" style="width:${size.width}px;height:${size.height}px;"></img>`
      );
      marker.setOffset(new AMap.Pixel(-size.width / 2, -size.height));
      marker.setzIndex(isSelected ? 110 : 100);
    };

    const prevSelectedMarker = prevSelectedIdRef.current
      ? markersRef.current.find(
          (m) => m.getExtData().id === prevSelectedIdRef.current
        )
      : null;
    if (prevSelectedMarker) {
      updateMarkerStyle(prevSelectedMarker, false);
    }

    const currentSelectedMarker = selectedId
      ? markersRef.current.find((m) => m.getExtData().id === selectedId)
      : null;
    if (currentSelectedMarker) {
      updateMarkerStyle(currentSelectedMarker, true);
      const position = currentSelectedMarker.getPosition();
      if (position) {
        mapRef.current.setCenter(position);
      }
    }

    prevSelectedIdRef.current = selectedId ?? null;
  }, [isMapReady, selectedId]);

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
      <Box
        id={containerIdRef.current}
        sx={{ width: "100%", height: "100%", borderRadius: "3px" }}
      />
      <LayerSwitch map={mapRef.current} style={{ right: "30px" }}></LayerSwitch>
      <WorldZoom map={mapRef.current} style={{ right: "30px" }}></WorldZoom>
      <Zooms map={mapRef.current} style={{ right: "30px" }}></Zooms>
    </Box>
  );
}

export default AMapNoMatch;
 